import React from "react";
import { Space } from "antd";
import { AxAlert, AxDivider, AxButton } from "@dynata/axiom3";

class Alert extends React.Component {
  render() {
    return (
      <div className="section" id="alert">
        <h3>Alert</h3>
        <Space direction="vertical">
          <p>Alert component for feedback.</p>
        </Space>

        <AxDivider orientation="left" plain>
          Basic Usage
        </AxDivider>

        <Space size="large" direction="vertical">
          <pre>{` <AxAlert message="Success Text" type="success" />`}</pre>

          <div className="prop-header">
            <p className="props-title">Props</p>
            <p>
              For AntD props, visit{" "}
              <AxButton
                size="small"
                type="link"
                target="_blank"
                href="https://ant.design/components/alert/#API"
              >
                https://ant.design/components/alert/#API
              </AxButton>
            </p>
          </div>
          <table className="props-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Description</th>
                <th>Type</th>
                <th>Default</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>action</td>
                <td>The action of Alert</td>
                <td>ReactNode</td>
                <td>-</td>
                <td>4.9.0</td>
              </tr>
              <tr>
                <td>afterClose</td>
                <td>Called when close animation is finished</td>
                <td>() =&gt; void</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>closable</td>
                <td>Whether Alert can be closed</td>
                <td>boolean</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>closeText</td>
                <td>Close text to show</td>
                <td>ReactNode</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>description</td>
                <td>Additional content of Alert</td>
                <td>ReactNode</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>message</td>
                <td>Content of Alert</td>
                <td>ReactNode</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>showIcon</td>
                <td>Whether to show icon</td>
                <td>boolean</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>type</td>
                <td>
                  Type of Alert styles, options: <code>success</code>,{" "}
                  <code>info</code>, <code>warning</code>, <code>error</code>
                </td>
                <td>string</td>
                <td>
                  <code>info</code>
                </td>
                <td></td>
              </tr>
              <tr>
                <td>onClose</td>
                <td>Callback when Alert is closed</td>
                <td>(e: MouseEvent) =&gt; void</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>...others</td>
                <td>
                  All extra props are passed down to the {" "}
                  <>
                    <AxButton
                      size="small"
                      type="link"
                      target="_blank"
                      href="https://4x.ant.design/components/alert/#API"
                    >
                      {`<Alert />`}
                    </AxButton>
                  </>{" "}
                  component.
                </td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>

          <div className="prop-header">
            <p className="props-title">Props: Alert.ErrorBoundary</p>
          </div>
          <table className="props-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Description</th>
                <th>Type</th>
                <th>Default</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>description</td>
                <td>Custom error description to show</td>
                <td>ReactNode</td>
                <td>
                  <code>{`{{ error stack }}`}</code>
                </td>
                <td></td>
              </tr>
              <tr>
                <td>message</td>
                <td>Custom error message to show</td>
                <td>ReactNode</td>
                <td>
                  <code>{`{{ error }}`}</code>
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </Space>

        <AxDivider orientation="left" plain>
          Success | Info | Warning | Error
        </AxDivider>

        <Space size="large" direction="vertical">
          <pre>{`
<AxAlert message="Success Text" type="success" />
<AxAlert message="Info Text" type="info" />
<AxAlert message="Warning Text" type="warning" />
<AxAlert message="Error Text" type="error" />
`}</pre>
          <Space direction="vertical">
            <AxAlert message="Success Text" type="success" />
            <AxAlert message="Info Text" type="info" />
            <AxAlert message="Warning Text" type="warning" />
            <AxAlert message="Error Text" type="error" />
          </Space>
        </Space>

        <AxDivider orientation="left" plain>
          With Icons
        </AxDivider>

        <Space size="large" direction="vertical">
          <pre>{`
<AxAlert message="Success Text" type="success" showIcon />
<AxAlert message="Info Text" type="info" showIcon />
<AxAlert message="Warning Text" type="warning" showIcon />
<AxAlert message="Error Text" type="error" showIcon />
`}</pre>
          <Space direction="vertical">
            <AxAlert message="Success Text" type="success" showIcon />
            <AxAlert message="Info Text" type="info" showIcon />
            <AxAlert message="Warning Text" type="warning" showIcon />
            <AxAlert message="Error Text" type="error" showIcon />
          </Space>
        </Space>

        <AxDivider orientation="left" plain>
          With Description
        </AxDivider>

        <Space size="large" direction="vertical">
          <pre>{`
<AxAlert
  message="Success Tips"
  description="Detailed description and advice about successful copywriting."
  type="success"
  showIcon
  closeText="Close Now"
/>
<AxAlert
  message="Informational Notes"
  description="Additional description and information about copywriting."
  type="info"
  showIcon
/>
<AxAlert
  message="Warning"
  description="This is a warning notice about copywriting."
  type="warning"
  showIcon
  closable
/>
<AxAlert
  message="Error"
  description="This is an error message about copywriting."
  type="error"
  showIcon
/>
`}</pre>
          <Space direction="vertical">
            <AxAlert
              message="Success Tips"
              description="Detailed description and advice about successful copywriting."
              type="success"
              showIcon
              closeText="Close Now"
            />
            <AxAlert
              message="Informational Notes"
              description="Additional description and information about copywriting."
              type="info"
              showIcon
            />
            <AxAlert
              message="Warning"
              description="This is a warning notice about copywriting."
              type="warning"
              showIcon
              closable
            />
            <AxAlert
              message="Error"
              description="This is an error message about copywriting."
              type="error"
              showIcon
            />
          </Space>
        </Space>
      </div>
    );
  }
}
export default Alert;
